import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  PhoneCall,
  FileText,
  Mail,
  Users,
  BookOpen,
  Video,
  Database,
  MessageCircle,
  User,
  MessageSquare,
  Clock,
  Linkedin,
} from "lucide-react";

const benefits = [
  {
    icon: User,
    title: "1:1 Personal Guidance",
    description:
      "By Charchit Kurra (yup, that LinkedIn guy with actual experience)",
  },
  {
    icon: Mail,
    title: "Cold Mailing Strategies",
    description: "That get replies, not seen-zones",
  },
  {
    icon: Linkedin,
    title: "LinkedIn Outreach Tricks",
    description: "To get in the right inboxes",
  },
  {
    icon: FileText,
    title: "Resume Glow-ups",
    description: "That won't make HRs yawn",
  },
  {
    icon: Clock,
    title: "Regular Follow-ups & Support",
    description: "We won't ghost you",
  },
  {
    icon: MessageSquare,
    title: "End-to-end Help",
    description: "From confused to confident",
  },
];

const InternshipWhatYoullGet = (details) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="benefits" className="py-24 relative overflow-hidden">
      {/* Background with dots + gradient overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "radial-gradient(#404040 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 20%, black 70%)",
        }}
      />

      {/* Content sits on top of background */}
      <div className="container mx-auto px-2 md:px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="font-style-gradient">What You'll Get</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to land your dream internship in just 60 days
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.2 },
                }}
                className="group w-full max-w-sm"
              >
                <div className="bg-glass-gradient backdrop-blur-glass border border-purple-500/15 rounded-2xl p-6 h-full hover:border-purple-500/50 transition-all duration-300 hover:shadow-glow">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-3 rounded-xl bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                      <Icon className="h-8 w-8 text-purple-500" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg text-foreground">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Call-to-action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center px-2 py-2 md:px-6 md:py-3 rounded-full bg-purple-500/10 border border-purple-500/20 backdrop-blur-sm hover:bg-purple-500/15 hover:border-purple-500/30 transition-all duration-300">
            <span className="text-purple-500 font-medium">
              ✨ This is not a mass webinar. It is a hands-on cohort with actual
              accountability.
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InternshipWhatYoullGet;
